import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";

export function StoredBeauftragungPreviewDialog({
  open,
  title,
  description,
  load,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: string | null;
  load: () => Promise<Blob>;
  onClose: () => void;
}) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setBlob(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlob(null);
    void load()
      .then((next) => {
        if (!cancelled) setBlob(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Beauftragung konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, load]);

  const isImage = Boolean(blob && blob.type.toLowerCase().startsWith("image/"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description || undefined}
      size="4xl"
      fullScreenMobile
      bodyClassName="flex min-h-0 flex-col"
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      <div className="min-h-[min(72vh,56rem)]">
        {loading ? (
          <div className="flex min-h-[min(72vh,56rem)] items-center justify-center">
            <Spinner />
          </div>
        ) : null}
        {error ? (
          <p className="flex min-h-[12rem] items-center justify-center px-4 text-center text-sm text-status-red">
            {error}
          </p>
        ) : null}
        {blob && !loading && !error && isImage ? <ImagePreview blob={blob} title={title} /> : null}
        {blob && !loading && !error && !isImage ? (
          <InlinePdfView
            blob={blob}
            title={title}
            className="min-h-[min(72vh,56rem)]"
          />
        ) : null}
      </div>
    </Dialog>
  );
}

function ImagePreview({ blob, title }: { blob: Blob; title: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  if (!url) return null;
  return (
    <div className="flex min-h-[min(40vh,24rem)] items-center justify-center p-3">
      <img src={url} alt={title} className="max-h-[min(72vh,48rem)] max-w-full object-contain" />
    </div>
  );
}
